// Report Generator types
import type { CurrencyType, DateRangeValue, ExchangeMethod, ICubicData, ReportTableColumn } from "store/types";

import type { Chart, ChartType } from "./api";

// ============================================
// Card Data Types
// ============================================

export interface TableColumn {
  key: string;
  title: string;
  postfix?: string;
  aggregation?: string;
  dateRange?: DateRangeValue;
  currency?: CurrencyType;
}

export interface TableRow {
  title?: string;
  valueData?: Record<string, number | null>;
  children?: TableRow[];
  // Set on the re-emitted parent of a group that continues from a previous page.
  // Its own row is not drawn again (avoids duplicating the group header); it only
  // carries the remaining children so their indentation depth is preserved.
  isContinuationGroup?: boolean;
  // Stable canonical position in the full table (e.g. "0.2.1"), assigned before
  // pagination. Used to look up the row's DOM-measured height. Preserved across
  // slicing (spread copies it), so a row keeps the same key in every chunk.
  __path?: string;
  [key: string]: string | number | boolean | null | Record<string, number | null> | TableRow[] | undefined;
}

export interface DataTableCardData {
  columns: Array<string | TableColumn>;
  data: TableRow[];
}

export type KPICardData = Partial<ICubicData>;

export interface SummaryCardData {
  parameter?: string;
  total?: string | number;
  average?: string | number;
  min?: string | number;
  max?: string | number;
  value?: string | number;
  measureName?: string;
  title?: string;
}

export interface TextCardData {
  content: string | null;
}

export interface AICardData {
  answer?: string;
}

export interface UnfundedCardData {
  name: string;
  committed: number;
  called: number;
  postfix: string;
}

export interface CashFlowTimelineCardData {
  quarter: string;
  called: number;
  distributed: number;
  postfix: string;
}

export interface DataFreshnessEntry {
  id: number;
  title: string;
  // Null when the deal has no NAV date yet.
  lastNavDate: string | null;
}

export interface DataFreshnessCardData {
  dataFreshness: DataFreshnessEntry[];
}

export interface PerformanceAttributionCardData {
  portfolio: {
    deals: Array<{
      name: string;
      value: number;
    }>;
  };
}

export interface MacroBriefIndicator {
  key: string;
  label: string;
  value: number;
  prior_value: number;
  prefix: string;
  source: string;
  date: string;
}

export interface MacroBriefCardData {
  indicators: MacroBriefIndicator[];
  narrative: string;
}

export interface MacroBriefAvailableIndicator {
  key: string;
  label: string;
  category: string;
}

export interface BenchmarkComparisonEntry {
  name: string;
  value: number;
  type: "portfolio" | "peer" | "market";
  postfix: string;
}
export type BenchmarkComparisonCardData = BenchmarkComparisonEntry[];

// Union type for all card data
export type CardData =
  | Chart
  | DataTableCardData
  | SummaryCardData
  | KPICardData
  | TextCardData
  | AICardData
  | UnfundedCardData[]
  | CashFlowTimelineCardData[]
  | DataFreshnessCardData
  | PerformanceAttributionCardData
  | MacroBriefCardData
  | ExposureCardData
  | BenchmarkComparisonCardData;

// ============================================
// Card Types & Dimensions
// ============================================

export type CardType =
  | "kpi"
  | "summary"
  | "chart"
  | "table"
  | "text"
  | "ai_card"
  | "exposure"
  | "cashflow"
  | "unfunded"
  | "performance"
  | "benchmark"
  | "dataFreshness"
  | "macroBrief";

export type MetricKey =
  | "totalNAV"
  | "totalCommitted"
  | "totalCalled"
  | "totalDistributed"
  | "irr"
  | "moic"
  | "dpi"
  | "rvpi";

export type PieDimension = "assetClass" | "strategy";

export type ExposureDimension = "assetClass" | "Geography" | "Strategy" | "Vintage";

export type ExposureMeasure = "exposure" | "adjNav" | "unfundedCommitment";
export type ExposureChartType = "bar" | "donut" | "pie" | "column" | "treemap";
export type Period = "ITD" | "YTD" | "1Y" | "3Y";
export type Currency = "USD" | "EUR" | "GBP";
export type SizePreset = "S" | "M" | "F";
export type ReportOrientation = "portrait" | "landscape";

export type MacroIndicatorKey = string;

// ============================================
// Deal & Portfolio Data
// ============================================

export interface RoundInvestor {
  investorName: string;
  commitment: number;
}

export interface Round {
  id: string;
  name: string;
  investors: RoundInvestor[];
}

export interface Deal {
  id: string;
  name: string;
  assetClass: string;
  strategy: string;
  vintage: number;
  geography: string;
  nav: number;
  committed: number;
  called: number;
  distributed: number;
  irr: number;
  moic: number;
  dpi: number;
  rvpi: number;
  rounds: Round[];
}

export interface PortfolioData {
  reportTitle: string;
  asOfDate: string;
  totalNAV: number;
  totalCommitted: number;
  totalCalled: number;
  totalDistributed: number;
  irr: number;
  moic: number;
  dpi: number;
  rvpi: number;
  deals: Deal[];
}

export interface Filters {
  investors: string[];
  // assetClasses: string[];
  // Dynamic report filters (round, deal_name, etc.) selected via the InlineFilter. Keyed by the
  // backend filter name, matching the shape sent to the reports endpoints. Investor is kept out of
  // here — it is surfaced as its own chip.
  reportFilters?: Record<string, string[]>;
}

// ============================================
// Card Configs
// ============================================

export interface CardFilters {
  investors: string[];
  // assetClasses: string[];
  // Per-card dynamic report filters (round, deal_name, etc.), mirroring Filters.reportFilters.
  // When set they take priority over the global reportFilters for the matching dimension.
  reportFilters?: Record<string, string[]>;
  currency?: CurrencyType;
  exchangeMethod?: ExchangeMethod;
  dateRange?: DateRangeValue | null;
}

export interface KpiCardConfig {
  type: "kpi";
  metrics?: MetricKey[];
  cubics?: Record<string, { title: string; filter: string; indexName?: { index: string; name: string } }>;
  cardFilters?: CardFilters;
}

export interface SummaryCardConfig {
  type: "summary";
  metrics?: MetricKey[];
  parameter?: string;
  cardFilters?: CardFilters;
}

export interface ChartCardConfig {
  type: "chart";
  chartType: ChartType;
  pieDimension?: PieDimension;
  graphDataFormation?: Record<
    string,
    {
      measure: string;
      groupBy: string;
      chartType: ChartType;
      xAxis?: { name: string; type: string };
      yAxis?: { name: string; type: string };
    }
  >;
  cardFilters?: CardFilters;
}

export type TableColumnKey =
  | "name"
  | "assetClass"
  | "strategy"
  | "vintage"
  | "geography"
  | "nav"
  | "committed"
  | "called"
  | "distributed"
  | "irr"
  | "moic"
  | "dpi"
  | "rvpi";

export interface TableCardConfig {
  type: "table";
  visibleColumns?: TableColumnKey[];
  measures?: string[];
  calculatedFields?: string[];
  characteristics?: string[];
  columns?: ReportTableColumn[];
  groupBy?: string[][];
  rawGroupBy?: string[];
  columnBy?: "measure";
  rowBy?: "deal" | "investor" | "measure";
  indexName?: string;
  columnWidths?: Record<string, number>;
  cardFilters?: CardFilters;
}

export interface TextCardConfig {
  type: "text";
  content: string;
  cardFilters?: CardFilters;
}

export interface AICardConfig {
  type: "ai_card";
  question: string;
  answer?: string;
  cardFilters?: CardFilters;
  // When true the question is collapsed in edit mode and omitted from the
  // exported PDF — only the answer is shown.
  hideQuestion?: boolean;
}

export interface ExposureCardConfig {
  type: "exposure";
  dimension: ExposureDimension;
  chartType: ExposureChartType;
  measure: ExposureMeasure;
  cardFilters?: CardFilters;
}

export type ExposureCardData = Array<{ label: string; value: number; percentage: number; postfix: string }>;

export interface CashFlowCardConfig {
  type: "cashflow";
  cardFilters?: CardFilters;
}

export interface UnfundedCardConfig {
  type: "unfunded";
  cardFilters?: CardFilters;
}

export interface PerformanceCardConfig {
  type: "performance";
  sortBy: "irr" | "moic" | "dpi" | "rvpi";
  topN: number;
  cardFilters?: CardFilters;
}

export interface BenchmarkCardConfig {
  type: "benchmark";
  benchmarkIds: string[];
  cardFilters?: CardFilters;
}

export type DataFreshnessTimeRange = "month" | "quarter" | "year";

export interface DataFreshnessCardConfig {
  type: "dataFreshness";
  // One of the calendar periods (Monthly / Quarterly / Annual). Resolved to concrete from/to dates
  // when the card is fetched.
  timeRange?: DataFreshnessTimeRange;
  cardFilters?: CardFilters;
}

export interface MacroBriefCardConfig {
  type: "macroBrief";
  indicators: MacroIndicatorKey[];
  showNarrative?: boolean;
  cardFilters?: CardFilters;
}

export type CardConfig =
  | KpiCardConfig
  | SummaryCardConfig
  | ChartCardConfig
  | TableCardConfig
  | TextCardConfig
  | AICardConfig
  | ExposureCardConfig
  | CashFlowCardConfig
  | UnfundedCardConfig
  | PerformanceCardConfig
  | BenchmarkCardConfig
  | DataFreshnessCardConfig
  | MacroBriefCardConfig;

// ============================================
// Canvas Card
// ============================================

export interface CanvasCard {
  id: string;
  type: CardType;
  // Grid coordinates (source of truth)
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
  // Derived / legacy
  widthPercent: number;
  heightPx?: number;
  lastPreset: SizePreset | null;
  rowIndex: number;
  orderInRow: number;
  config: CardConfig;
  page: number;
  data?: CardData;
  loading?: boolean;
}

export const PRESET_GRID_W: Record<SizePreset, number> = { S: 4, M: 6, F: 12 };

export const DEFAULT_GRID_H: Record<CardType, number> = {
  kpi: 2,
  summary: 2,
  chart: 4,
  table: 8,
  text: 3,
  ai_card: 3,
  exposure: 4,
  cashflow: 4,
  unfunded: 4,
  performance: 4,
  benchmark: 4,
  dataFreshness: 3,
  macroBrief: 3
};

// ============================================
// Page Layouts
// ============================================

export type PageLayout = "title" | "header-content" | "content" | "section";

export interface ReportPage {
  layout: PageLayout;
  // Per-page title/subtitle. On section-break pages `title` is the section title. On title and
  // header pages `title`/`subtitle` hold the page's own heading — seeded (snapshotted) at page
  // creation from the nearest preceding title page, then independently editable. Legacy pages
  // without these fall back to the global reportMeta title/subtitle at render time.
  title?: string;
  subtitle?: string;
}

// ============================================
// Report Metadata
// ============================================

export interface ReportMeta {
  title: string;
  subtitle: string;
  asOfDate: string;
  preparedBy: string;
  logo: string;
  // Shared background image applied to every non-title page (header-content, content, section break).
  background?: string;
  backgroundOpacity?: number;
}

// ============================================
// Views
// ============================================

export interface View {
  id: string;
  name: string;
  description?: string;
  filters: Filters;
  cards: CanvasCard[];
  period: DateRangeValue | null;
  currency: Currency;
  reportMeta: ReportMeta;
  pages: ReportPage[];
  orientation: ReportOrientation;
}

// ============================================
// Image Search Types
// ============================================

export interface SearchImageResult {
  thumb: string;
  regular: string;
  full: string;
}

// ============================================
// Template Types (API)
// ============================================

export interface TemplateContentSummary {
  card_count: number;
  orientation: ReportOrientation;
  currency: Currency;
  period: DateRangeValue | null;
  filter_investors: string[];
  filter_asset_classes: string[];
}

export interface ReportGeneratorTemplateListItem {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  content_summary: string | null;
}

export interface ReportGeneratorTemplateFile {
  id: number;
  name: string;
  file_url: string;
  type: "logo" | "background";
}

export interface PageMeta {
  backgroundOpacity?: number;
}

export type TemplateType = "template" | "last_view";

export interface ReportGeneratorTemplate {
  id: number;
  name: string;
  description?: string | null;
  deal_id?: number | null;
  report_title: string | null;
  content: ReportGeneratorSavedCard[][] | string | null;
  content_metadata: PageMeta | string | null;
  files: ReportGeneratorTemplateFile[];
  created_at: string;
  template_type?: TemplateType;
}

export interface ReportGeneratorTemplateSavePayload {
  name: string;
  description?: string;
  deal_id?: number;
  report_title?: string;
  content?: string;
  content_metadata?: string;
  content_summary?: string;
  logo?: File;
  logo_url?: string;
  background?: File;
  background_url?: string;
  template_type?: TemplateType;
}

export interface ReportGeneratorSavedCard {
  type: string;
  title: string;
  gridCol: number;
  gridRow: number;
  gridSpan: number;
  gridRowSpan: number;
  customConfig?: Record<string, unknown>;
}

// ============================================
// Constants
// ============================================

export const GRID_CONSTANTS = {
  CANVAS_WIDTH: 642,
  CANVAS_HEIGHT: 971,
  GRID_COLUMNS: 12,
  GAP: 12,
  GRID_CELL_HEIGHT: 60,
  MARGIN: 20,
  HEADER_HEIGHT: 80,
  FOOTER_HEIGHT: 40,
  ALIGN_SNAP_TOLERANCE: 4
} as const;
